import { KeyboardEvent, SyntheticEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { helpers } from "../../../../packages/helpers";
import {
  EMPTY_MESSAGE,
  MESSAGES_ADD_DIRECTIONS,
} from "../../../../packages/constants";
import { useControlContext } from "../../../control";
import { authSelectors } from "../../../auth";
import { useNetworkContext } from "../../../network";
import {
  createMessageText,
  createMessageFile,
  getSendMessageTextData,
  getSendMessageFileData,
} from "../../providers";
import {
  addMessages,
  getMessages,
  postMessageFile,
  postMessageText,
} from "../../actions";
import { isMessageFile } from "../../type-guards";
import { selectChatId } from "../../selectors";
import { useReplyMessage } from "../message-kinds";
import { useFileController } from "./files-controller";
import { useTextController } from "./text-controller";
import type {
  TextDataForMessage,
  Message,
  PostMessageData,
  PostMessageFileData,
} from "../../types";

const { getStringValue } = helpers.text;
const { selectUserId } = authSelectors;

export const useChatInput = () => {
  const dispatch = useAppDispatch();
  const { api } = useControlContext();
  const authorId = useAppSelector(selectUserId);
  const chatId = useAppSelector(selectChatId);

  const { isGetMessagesPending } = useNetworkContext();
  const { handleClearReplyMessage, replyMessage } = useReplyMessage();

  const { message, hasMessage, handleAddNewLine, handleClean, handleChange } =
    useTextController();

  const {
    files,
    hasFiles,
    hasInvalidFiles,
    attachedFilesCount,
    isFileLimitExceeded,
    isFileAttached,
    isFileLimitExhausted,

    fileInputRef,

    handleFileInputChange,
    handleRemoveFile,
    handleCleanFiles,
  } = useFileController();

  const { getMessagesApi, postMessageTextApi, postMessageFileApi } = api;

  const addMessageIntoStore = (
    msg: Message,
    fileLink?: string,
    mime?: string
  ) => {
    const msgContent = isMessageFile(msg)
      ? {
          ...msg.content,
          file: {
            ...msg.content.file,
            data: fileLink,
            mime: getStringValue(mime),
          },
        }
      : msg.content;

    const params = {
      messages: [{ ...msg, content: msgContent }],
      direction: MESSAGES_ADD_DIRECTIONS.END,
    };

    dispatch(addMessages(params));
  };

  const sendMessage = (msg: PostMessageData, traceId: string) => {
    const params = {
      postMessageTextApi,

      traceId,
      message: msg,
    };

    dispatch(postMessageText(params));
  };

  const sendMessageFile = async (msg: PostMessageFileData, traceId: string) => {
    const params = {
      postMessageFileApi,

      traceId,
      message: msg,
    };

    dispatch(postMessageFile(params));
  };

  const sendMessageCallback = async () => {
    if (isGetMessagesPending) {
      const params = { getMessagesApi, chatId };

      await dispatch(getMessages(params));
    }

    if (hasFiles) {
      // Очистка файлов от невалидных и разделение на Первое и Остальные
      const [firstFileMessage, ...otherFileMessages] = files.filter(
        ({ error }) => !error
      );

      // Коллбек на генерацию и добавление сообщений в store
      const addMessageIntoStoreCallback =
        (message: TextDataForMessage) =>
        async ({ file }: { file: File }) => {
          const fileLink = URL.createObjectURL(file);
          const params = { authorId, file, message, replyMessage };
          const messageFileToAdd = createMessageFile(params);

          addMessageIntoStore(messageFileToAdd, fileLink, file.type);

          return messageFileToAdd;
        };

      // Добавление Первого сообщения со статусом SEND в redux-store перед отправкой
      const firstFileMessageToAdd = await addMessageIntoStoreCallback(message)(
        firstFileMessage
      );

      // Добавление Остальных сообщений со статусом SEND в redux-store перед отправкой
      const otherFileMessagesToAddPromises = otherFileMessages.map(
        addMessageIntoStoreCallback(EMPTY_MESSAGE)
      );

      const otherFileMessagesToAdd = await Promise.all(
        otherFileMessagesToAddPromises
      );

      // Отправка Первого сообщения
      const firstFile = firstFileMessage.file;
      const { traceId: firstMessageTraceId } = firstFileMessageToAdd;

      const fileMessageToSend = getSendMessageFileData(
        firstFileMessageToAdd,
        firstFile
      );

      await sendMessageFile(fileMessageToSend, firstMessageTraceId);

      // Отправка Остальных сообщений
      const otherFileMessageToSendPromises = otherFileMessagesToAdd.map(
        (message, index: number) => {
          const { file } = otherFileMessages[index];

          const fileMessageToSend = getSendMessageFileData(message, file);

          return sendMessageFile(fileMessageToSend, message.traceId);
        }
      );

      await Promise.all(otherFileMessageToSendPromises);
    } else {
      // Добавление сообщения с статусом SEND в redux-store перед отправкой
      const params = { authorId, message, replyMessage };
      const messageTextToAdd = createMessageText(params);

      addMessageIntoStore(messageTextToAdd);

      // Отправка сообщения
      const messageTextToSend = getSendMessageTextData(messageTextToAdd);

      const { traceId } = messageTextToAdd;

      sendMessage(messageTextToSend, traceId);
    }

    // Очистка инпута
    handleClean();
    handleCleanFiles();
    handleClearReplyMessage();
  };

  const handleKeyDown = async (e: KeyboardEvent) => {
    const isEnter = e.key === "Enter";
    const isExtraAlt = e.altKey;
    const hasSomeToSend = hasMessage || hasFiles;

    if (isEnter && isExtraAlt) {
      e.preventDefault();

      handleAddNewLine();

      return;
    }

    if (isEnter && hasSomeToSend) {
      e.preventDefault();
      await sendMessageCallback();
    }
  };

  const handleClick = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!hasMessage && !hasFiles) {
      return;
    }

    await sendMessageCallback();
  };

  return {
    message,
    hasMessage,

    files,
    attachedFilesCount,
    hasFiles,
    hasInvalidFiles,

    isFileAttached,
    isFileLimitExhausted,
    isFileLimitExceeded,
    fileInputRef,

    // TODO cleanMessage использовать для очистки сообщения в инпуте по крестику
    handleClean,
    handleChange,
    handleClick,
    handleKeyDown,

    handleFileInputChange,
    handleRemoveFile,
  };
};
